export interface DiscountValidationResponse {
    emailBound?: boolean
    valid: boolean
    discountType: 'percentage' | 'fixed' | 'free_addon' | 'free_delivery'
    discountValue: number
    code: string
    error?: string
    freeAddonIds?: string[]
    freeAddonTypes?: string[]
    label?: string
    description?: string
}

const BACKEND_API_URL = (process.env.REACT_APP_BACKEND_API_URL || '').replace(/\/$/, '')

const getShopEnvironment = (): string => {
    return (process.env.REACT_APP_ENVIRONMENT || process.env.ENVIRONMENT || 'prod').trim().toLowerCase()
}

const getRegionFromPathname = (): string => {
    const firstSegment = (window.location.pathname.split('/').filter(Boolean)[0] || '').toLowerCase()
    // Region routes are 2-letter segments like /gb, /us, /de.
    return /^[a-z]{2}$/.test(firstSegment) ? firstSegment : 'gb'
}

const getDiscountValidation = async (
    code: string,
    options?: { source?: 'discount_code' | 'affiliate_link' }
): Promise<DiscountValidationResponse | null> => {
    if (!BACKEND_API_URL) return null

    try {
        const environment = getShopEnvironment()
        const params = new URLSearchParams()
        if (environment !== 'prod') {
            params.set('testMode', environment)
        }
        const response = await fetch(`${BACKEND_API_URL}/offers/discount/validate/${params.toString() ? `?${params.toString()}` : ''}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(
                options?.source === 'affiliate_link'
                    ? { affiliateCode: code, region: getRegionFromPathname() }
                    : { code, region: getRegionFromPathname() }
            )
        })

        if (!response.ok) return null
        const payload = await response.json()
        if (!payload) return null

        return {
            emailBound: Boolean(payload.emailBound),
            valid: Boolean(payload.valid),
            discountType: payload.discountType || 'free_addon',
            discountValue: Number(payload.discountValue || 0),
            code: payload.code || code,
            error: payload.error,
            label: payload.label || '',
            freeAddonIds: Array.isArray(payload.freeAddonIds) ? payload.freeAddonIds.filter(Boolean) : [],
            freeAddonTypes: Array.isArray(payload.freeAddonTypes) ? payload.freeAddonTypes.filter(Boolean) : [],
            description: payload.description
        }
    } catch {
        return null
    }
}

export const resolveAffiliateLinkDiscountCode = async (affiliateCode: string): Promise<DiscountValidationResponse | null> => {
    return getDiscountValidation(affiliateCode, { source: 'affiliate_link' })
}

export const validateDiscountCode = async (code: string): Promise<DiscountValidationResponse> => {
    const result = await getDiscountValidation(code)
    if (result) {
        return result
    }

    return {
        valid: false,
        discountType: 'percentage',
        discountValue: 0,
        code,
        error: 'Invalid discount code'
    }
}
